import { useState, useEffect } from 'react';
import type { Photo, User } from '../../types';
import { isResolvedCredit } from '../../utils/photoCredits';
import { apiFetch } from '../../utils/api';
import { authHeaders } from '../../utils/auth';

interface Props {
  photo: Photo;
  batchId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CorrectTagsModal({ photo, batchId, onClose, onSuccess }: Props) {
  const [allArtists, setAllArtists] = useState<User[]>([]);
  const [removeArtistId, setRemoveArtistId] = useState<number | null>(null);
  const [addArtistId, setAddArtistId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const credits = (photo.credits ?? []).filter(
    (c): c is typeof c & { userId: { id: number; fName: string; lName: string } } =>
      isResolvedCredit(c.userId),
  );
  const taggedIds = new Set(credits.map(c => (c.userId as { id: number }).id));
  const replacementRequired = credits.length === 1;

  useEffect(() => {
    apiFetch('/api/users?role=artist', { headers: authHeaders() })
      .then(r => r.json() as Promise<User[]>)
      .then(d => setAllArtists(d ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const availableToAdd = allArtists.filter(u => u.isActive && !taggedIds.has(u.id));

  const handleSubmit = async () => {
    if (removeArtistId === null) return;
    if (replacementRequired && addArtistId === '') return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/photos/${photo.id}/correct-tags`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batchId,
          removeArtistId,
          ...(addArtistId !== '' ? { addArtistId: Number(addArtistId) } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json() as { message?: string };
        setError(data.message ?? 'An error occurred.');
        return;
      }
      onSuccess();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='ai-modal-backdrop' onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className='ai-modal correct-tags-modal' role='dialog' aria-modal='true'>
        <div className='ai-modal-header'>
          <h2 className='ai-modal-title'>Correct Tags</h2>
          <button className='ai-modal-close' onClick={onClose} aria-label='Close' disabled={submitting}>×</button>
        </div>

        <p className='correct-tags-filename'>{photo.originalFilename}</p>

        <div className='correct-tags-section'>
          <p className='correct-tags-label'>Select artist to remove</p>
          {credits.length === 0 ? (
            <p className='correct-tags-empty'>No artists are currently tagged on this photo.</p>
          ) : (
            <div className='correct-tags-artist-list'>
              {credits.map(credit => {
                const artist = credit.userId as { id: number; fName: string; lName: string };
                const selected = removeArtistId === artist.id;
                return (
                  <button
                    key={artist.id}
                    type='button'
                    className={`correct-tags-artist-btn${selected ? ' selected' : ''}`}
                    onClick={() => setRemoveArtistId(selected ? null : artist.id)}
                  >
                    <span>{artist.fName} {artist.lName}</span>
                    <span className={`credit-badge credit-badge-${credit.approvalStatus}`}>
                      {credit.approvalStatus}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className='correct-tags-section'>
          <label className='correct-tags-label' htmlFor='correct-tags-add'>
            Add replacement artist{!replacementRequired && <span className='correct-tags-optional'> (optional)</span>}
          </label>
          <select
            id='correct-tags-add'
            className='correct-tags-select'
            value={addArtistId}
            onChange={e => setAddArtistId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value=''>— none —</option>
            {availableToAdd.map(u => (
              <option key={u.id} value={u.id}>{u.fName} {u.lName}</option>
            ))}
          </select>
        </div>

        <div className='correct-tags-warning'>
          {replacementRequired
            ? 'This is the only tagged artist. You must select a replacement before removing them.'
            : addArtistId !== ''
              ? "The replacement artist will receive a new link with a fresh 24-hour window. The removed artist's existing link remains active for any other photos they are credited on."
              : "The photo will be removed from the artist's gallery on their next visit. Their existing link and review window are unchanged."}
        </div>

        {error && <p className='correct-tags-error'>{error}</p>}

        <div className='modal-actions'>
          <button className='user-cancel-btn' onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className='batch-action-btn danger'
            onClick={handleSubmit}
            disabled={removeArtistId === null || (replacementRequired && addArtistId === '') || submitting}
          >
            {submitting ? 'Applying…' : 'Apply Correction'}
          </button>
        </div>
      </div>
    </div>
  );
}
